import React from "react";
import { Link } from "react-router-dom";
import { M2Layout, SectionIndex, FigCap, Arrow, CTABand } from "../../components/machines2/Blocks";
import { MachineStackFigure } from "../../components/machines2/MachineStackFigure";
import { HeroVideo } from "../../components/enterprise2/HeroVideo";
import { HairlineList } from "../../components/enterprise2/HairlineList";
import { PullQuote } from "../../components/enterprise2/EditorialPolish";
import { Reveal } from "../../components/enterprise2/Reveal";
import { SubNav } from "../../components/enterprise2/SubNav";
import { PRODUCTS, STEPS, APPROACH_HOME, INDUSTRY_LIST, ROADMAP_LABEL, ROADMAP_TO } from "../../data/machines2";

const SUBNAV = [
  { id: "stack", label: "The stack" },
  { id: "vision", label: "Vision" },
  { id: "platform", label: "Platform" },
  { id: "path", label: "The path" },
  { id: "industries", label: "Industries" },
  { id: "approach", label: "Approach" },
];

export default function Machines2Home() {
  return (
    <M2Layout active="home" title="Crowther for Machines | Physical AI on your own terms" description="One platform for building, deploying and operating intelligent machines, on infrastructure you control.">
      {/* Hero: centred, film below */}
      <section className="e2-hero-banner" data-testid="m2-hero">
        <div className="e2-hero-banner-grid">
          <div className="e2-hero-banner-lead">
            <div className="e2-eyebrow">For Machines</div>
            <h1 className="e2-hero-banner-title" data-testid="m2-hero-title">We are bringing intelligence to a billion machines.</h1>
            <p className="e2-hero-banner-subtitle">One platform for building, deploying and operating intelligent machines.</p>
            <div className="e2-hero-banner-actions">
              <Link to={ROADMAP_TO} className="e2-btn e2-btn-primary" data-testid="m2-hero-roadmap">{ROADMAP_LABEL}</Link>
              <Link to="/machines-2/contact" className="e2-btn-link" data-testid="m2-hero-contact">Talk to Crowther <Arrow /></Link>
            </div>
          </div>
          <div className="e2-hero-banner-side">
            <HeroVideo
              src="/media/crowther-corporate.mp4"
              poster="/media/machines-hero-poster.jpg"
              eyebrow={"FILM \u00a0\u00b7\u00a0 MACHINES ON SITE"}
              copy="The machines that build, move and grow the physical world are ready for intelligence of their own. Built on site, under your law, with your hands on the keys."
              label="Crowther for Machines film"
            />
          </div>
        </div>

        <Reveal className="e2-hero-feature" data-testid="m2-statement">
          <div className="eyebrow">The opening statement</div>
          <h2 className="title">The machines that build, move and grow the physical world are ready for intelligence of their own.</h2>
          <p className="sub">Intelligence can arrive as a rented service, governed by someone else&rsquo;s terms. Or it can arrive as a capability the owner holds. We exist to make the second future the normal one.</p>
          <div className="cta"><Link to="/machines-2/approach" className="e2-btn" data-testid="m2-statement-cta">How we build <Arrow /></Link></div>
        </Reveal>
      </section>

      <SubNav items={SUBNAV} testId="m2-subnav" />

      {/* Key visual, moved up: directly after the opening statement */}
      <Reveal as="section" id="stack" className="e2-shell e2-section" data-testid="m2-stack">
        <SectionIndex n="01" label="The sovereign machine stack" />
        <div className="e2-split">
          <div className="e2-split-title-col">
            <h2 className="e2-h-l">Every layer runs where the work happens.</h2>
            <p className="e2-body e2-split-lede">Five layers, one permission model. Intelligence lives on the machine and on your site, and the bottom line is not a slogan: it is who holds the keys.</p>
          </div>
          <div>
            <FigCap n="1" label="The sovereign machine stack" />
            <MachineStackFigure />
          </div>
        </div>
      </Reveal>

      {/* Vision */}
      <Reveal as="section" id="vision" className="e2-shell e2-section" data-testid="m2-vision">
        <SectionIndex n="02" label="Our vision" />
        <div className="e2-split">
          <div className="e2-split-title-col">
            <h2 className="e2-h-l">Every machine will think. The question is who it thinks for.</h2>
          </div>
          <div>
            <p className="e2-body e2-body-lg">Haul trucks. Harvesters. Warehouse fleets. Gantry cranes. Assembly cells. Freight on the open road. One by one, the machines of the working world are gaining the ability to perceive, decide and act.</p>
            <p className="e2-body" style={{ marginTop: 18 }}>Almost all of that change is still ahead of us, and how it happens matters as much as whether it happens. Intelligence can arrive as a rented service, dependent on a distant cloud and governed by someone else&rsquo;s terms. Or it can arrive as a capability the owner holds: running on the machine, learning from the work, answering to the operation.</p>
            <p className="e2-body" style={{ marginTop: 18 }}>Crowther exists to make the second future the normal one. We build machine intelligence the way infrastructure should be built: on site, under your law, with your hands on the keys.</p>
            <Link to="/machines-2/approach" className="e2-btn-link" style={{ marginTop: 26, display: "inline-flex" }} data-testid="m2-vision-cta">Our approach <Arrow /></Link>
          </div>
        </div>
        <PullQuote mark="01">On site. On your terms.</PullQuote>
      </Reveal>

      {/* Platform */}
      <Reveal as="section" id="platform" className="e2-shell e2-section" data-testid="m2-platform">
        <SectionIndex n="03" label="The platform" />
        <div className="e2-lede">
          <h2 className="e2-h-l" style={{ maxWidth: "22ch" }}>One platform. Every machine class.</h2>
          <p className="e2-body e2-body-lg" style={{ maxWidth: "62ch", marginTop: 18 }}>Four products, built to work together or apart. Together they take a machine from manual operation to supervised intelligence to full autonomy, without surrendering the machine, the data or the site to anyone else.</p>
        </div>
        <FigCap n="2" label="The platform, top to bottom" style={{ marginTop: 40 }} />
        <HairlineList
          rows={PRODUCTS.map((p) => ({
            testId: `m2-platform-row-${p.key}`,
            name: <><span className="e2-idx-num" style={{ marginRight: 14 }}>{p.n}</span>{p.name}</>,
            desc: <><strong style={{ color: "var(--forest)", fontWeight: 500 }}>{p.heading}</strong> {p.short}</>,
            cta: <Link to="/machines-2/products" className="e2-row-cta" data-testid={`m2-platform-cta-${p.key}`}>Read the product <Arrow /></Link>,
          }))}
        />
      </Reveal>

      {/* The path */}
      <Reveal as="section" id="path" className="e2-shell e2-section" data-testid="m2-steps">
        <SectionIndex n="04" label="How it begins" />
        <div className="e2-split">
          <div className="e2-split-title-col">
            <h2 className="e2-h-l">Autonomy is earned, one stage at a time.</h2>
            <p className="e2-body e2-split-lede">No fleet becomes intelligent overnight, and no honest supplier will promise that it does. This is the path we walk with every operation.</p>
          </div>
          <div>
            {STEPS.map((s) => (
              <div key={s.n} className="e2-num-row" data-testid={`m2-step-${s.n}`}>
                <div className="num">{s.n}</div>
                <div className="body"><p><strong>{s.title}</strong></p><p>{s.body}</p></div>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      {/* Industries */}
      <Reveal as="section" id="industries" className="e2-shell e2-section" data-testid="m2-industries">
        <SectionIndex n="05" label="Industries" />
        <div className="e2-thinking-head" style={{ marginBottom: 36 }}>
          <h2 className="e2-h-l" style={{ maxWidth: "18ch" }}>Built for the work the world depends on.</h2>
          <Link to="/machines-2/industries" className="e2-btn e2-btn-outline" data-testid="m2-industries-all">All six sectors <Arrow /></Link>
        </div>
        <div className="e2-thinking-grid">
          {INDUSTRY_LIST.map((ind, i) => (
            <Link key={ind.slug} to={`/machines-2/industries/${ind.slug}`} className="e2-thinking-card" data-testid={`m2-industry-card-${ind.slug}`}>
              <div className={`e2-thinking-cover ${["forest", "navy", "teal"][i % 3]}`}>
                <span className="e2-thinking-cover-glow" aria-hidden />
                <span className="e2-thinking-cover-text">{ind.title}</span>
              </div>
              <div className="e2-thinking-body">
                <p className="e2-thinking-title">{ind.line}</p>
                <span className="e2-thinking-read">Read the sector view <Arrow /></span>
              </div>
            </Link>
          ))}
        </div>
      </Reveal>

      {/* Approach */}
      <Reveal as="section" id="approach" className="e2-shell e2-section" data-testid="m2-approach">
        <SectionIndex n="06" label="Approach" />
        <div className="e2-split">
          <div className="e2-split-title-col">
            <h2 className="e2-h-l">Built for machines that cannot fail quietly.</h2>
            <Link to="/machines-2/approach" className="e2-btn-link" style={{ marginTop: 22, display: "inline-flex" }} data-testid="m2-approach-cta">How we build <Arrow /></Link>
          </div>
          <div className="e2-arg-facts">
            {APPROACH_HOME.map((a, i) => (
              <div key={a.title} className="e2-arg-fact" data-testid={`m2-approach-fact-${i + 1}`}>
                <div className="e2-arg-fact-n">0{i + 1}</div>
                <div><div className="e2-arg-fact-b" style={{ fontSize: 22, marginBottom: 8 }}>{a.title}</div><p className="e2-body" style={{ margin: 0 }}>{a.body}</p></div>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      {/* Research + Two sides */}
      <Reveal as="section" className="e2-shell e2-section" data-testid="m2-research">
        <SectionIndex n="07" label="Research" />
        <div className="e2-split">
          <div className="e2-split-title-col">
            <h2 className="e2-h-l">The same discipline, pointed at machines.</h2>
          </div>
          <div>
            <p className="e2-body e2-body-lg">Crowther runs one research programme across both sides of the company. The habits that make our enterprise systems dependable, careful evaluation, reproducible results and honest reporting, are the same habits we bring to machine intelligence. We share what we have tested, and we deploy what we have proven, in that order.</p>
            <Link to="/enterprise-2/research" className="e2-btn-link" style={{ marginTop: 22, display: "inline-flex" }} data-testid="m2-research-cta">Read our research <Arrow /></Link>
          </div>
        </div>
      </Reveal>

      <Reveal className="e2-band e2-shell" data-testid="m2-two-sides" style={{ padding: "88px 56px" }}>
        <div className="e2-eyebrow">One company, two sides</div>
        <h2 className="e2-h-l" style={{ maxWidth: "20ch", marginTop: 16 }}>The intelligence that runs your machines can run your business too.</h2>
        <p className="e2-body" style={{ maxWidth: "62ch", marginTop: 18, color: "rgba(245,245,247,0.72)" }}>Crowther builds sovereign AI for people and enterprises as well as machines. Banks, insurers, telecommunications operators and regulators run our systems inside their own walls, on their own infrastructure, under their own keys. The rule is the same on the trading floor and the factory floor: your AI, your control.</p>
        <div className="e2-cband-statement-actions" style={{ justifyContent: "flex-start", marginTop: 30 }}>
          <Link to={ROADMAP_TO} className="e2-cband-btn e2-cband-btn-primary" data-testid="m2-two-sides-roadmap">{ROADMAP_LABEL}</Link>
          <Link to="/enterprise-2" className="e2-cband-btn e2-cband-btn-ghost" data-testid="m2-two-sides-enterprise">Visit For People &amp; Enterprises</Link>
        </div>
      </Reveal>

      {/* Careers */}
      <Reveal as="section" className="e2-shell e2-section" data-testid="m2-careers">
        <SectionIndex n="08" label="Build with us" />
        <div className="e2-split">
          <div className="e2-split-title-col">
            <h2 className="e2-h-l">Machines that think need builders who care.</h2>
          </div>
          <div>
            <p className="e2-body e2-body-lg">We are building the team that brings intelligence to working machines: engineers who respect physics, love constraints and want their software to answer to the people who use it. If autonomy with the owner in command is the problem you want to work on, we would like to hear from you.</p>
            <Link to="/machines-2/contact" className="e2-btn e2-btn-outline" style={{ marginTop: 26 }} data-testid="m2-careers-cta">Talk to Crowther <Arrow /></Link>
          </div>
        </div>
      </Reveal>

      <CTABand line="Find out more about Crowther for machines." sub="Tell us about your fleet and your site, and we will show you where intelligence pays for itself first." testId="m2-cta-band" />
    </M2Layout>
  );
}
